import { getDebuggingFlag } from '../../../shared/utils/debugging';
import { isNative, isWeb } from '../../../shared/utils/is';
import { MobileBaseField } from '../../features/mobileForm/BaseField/models';
import { ZenputFiles } from './files';

export const MobileMediaField = MobileBaseField.extend({
    submodels: {
        file: ZenputFiles
    },

    validation: {
        file: {
            required: function (val, attr, attrs) {
                return this._validate_field(val, attr, attrs);
            }
        }
    },

    /**
     * Our validation method for this field
     * If the field isn't required, always return that its valid (hidden fields for example)
     * otherwise check either the file or the value
     *
     * Return anything truthy to say that it is invalid.
     */
    _validate_field: function (val, attr, attrs) {
        var is_required = this._is_required(val, attr, attrs);
        if (!is_required) {
            return false;
        }
        if (this.has_value()) {
            return false;
        }
        return true;
    },

    initialize: function (attrs, options) {
        MobileMediaField.__super__.initialize.apply(this, arguments);

        if (!getDebuggingFlag('disable_background_uploading')) {
            this.on('change:file', this.queue_field_files_for_upload.bind(this));
        }
    },

    /**
     * Get a set of images from both this.get('file') and this.get('value').
     *
     * @return {ZenputFiles} The files, both local and remote
     */
    get_local_and_remote_files: function () {
        var files = new ZenputFiles();

        if (this.get('value')) {
            _.each(this.get('value'), function (url) {
                if ($f.flags.get_flag('detect_low_quality_photos')) {
                    const s3_key = typeof url === 'string' ? url : url.s3_key;
                    files.add({ url_s3: s3_key });
                } else {
                    files.add({ url_s3: url });
                }
            });
        }

        if (this.get('file')) {
            this.get('file').each(function (file) {
                files.add(file);
            });
        }
        return files;
    },

    hasLowQualityImages: (files) => {
        for (let model of files.models) {
            if (model.get('is_low_quality')) {
                return true;
            }
        }

        return false;
    },

    save_files: function (options, callback) {
        options = options || {};
        options.add = options.add || new ZenputFiles();
        options.modify = options.modify || new ZenputFiles();
        options.delete = options.delete || new ZenputFiles();
        callback = callback || function () {};
        var this_ = this;

        if (!options.add.length && !options.modify.length && !options.delete.length) {
            return;
        }

        var invalid_files = [];

        // Now, add/replace new images
        var _process_files = function (files, cb, i) {
            i = i || 0;

            if (i === files.length) {
                return cb();
            }

            files.at(i).process_for_zenput(
                function (file) {
                    _process_files(files, cb, i + 1);
                },
                function () {
                    // something went wrong, we should not save this file
                    invalid_files.push(files.at(i));
                    _process_files(files, cb, i + 1);
                }
            );
        };

        _process_files(options.add, function () {
            // process new files
            _process_files(options.modify, function () {
                // process modified files
                // remove any files we've marked as invalid
                _.each(invalid_files, function (file) {
                    file.collection.remove(file);
                });

                this_._set_files(options);
                callback();
            });
        });
    },

    /**
     * After files have processed, set them to the model here. For image fields, we will merge old and new files. For
     * video, we will replace.
     */
    _set_files: function (options) {
        options = options || {};
        options.add = options.add || new ZenputFiles();
        options.modify = options.modify || new ZenputFiles();
        options.delete = options.delete || new ZenputFiles();
        var this_ = this;

        var done = function () {
            // trigger a change on the collection so we are persisted to disk
            this_.collection && this_.collection.trigger('change');
        };

        var model_files = this.get('file') || new ZenputFiles();

        // First, delete anything we don't want anymore
        var files = model_files.filter(function (local_file) {
            if (isWeb()) {
                return !options.delete.get(local_file.id);
            }
            // @author:david someday we can update the Zencam API to use IDs instead of comparing URLs
            // @see https://docs.google.com/document/d/1ll1RtpuKY8U2ZieOcMmM40wT5hIgaaffPyBAN5Egl3M
            return !options.delete.findWhere({ url_native: local_file.get_url_native() });
        });

        var remote_urls = _.filter(this.get('value') || [], function (remote_path) {
            var delete_file;
            if ($f.flags.get_flag('fix_form_draft_photo_deletion')) {
                if (isNative()) {
                    options.delete.each(function (model) {
                        if (!delete_file) {
                            delete_file = model.get('url_native').includes(remote_path.s3_key);
                        }
                    });
                } else {
                    delete_file = options.delete.findWhere({ url_s3: remote_path.s3_key });
                }
            } else {
                delete_file = options.delete.findWhere({ url_s3: remote_path });
            }
            if (!delete_file) {
                return remote_path.s3_key;
            }
            return null;
        });
        // Next, replace old file objects with new ones
        options.modify.each(function (modified_file) {
            let original_file;
            if (isWeb()) {
                original_file = _.find(files, function (f) {
                    return f.get('id') === modified_file.get('id');
                });
            } else {
                original_file = _.find(files, function (f) {
                    return f.get_url_native() === modified_file.get('original_url_native');
                });

                if (!original_file) {
                    $f.mobile.logging.log_error('Unable to find original file after editing in Zencam', {
                        original_url_native: modified_file.get('original_url_native')
                    });

                    return true;
                }
            }
            // Replace the file!
            files.splice(files.indexOf(original_file), 1, modified_file);
        });

        // Save the change, which will re-trigger background uploads
        this.set({
            file: files.length ? new ZenputFiles(files) : null,
            value: remote_urls.length ? remote_urls : ''
        });

        if (!options.add.length) {
            done();
            return;
        }
        // Last, set the file(s). This will prompt background uploads.
        if (this.get('type') === 'video' || (this.get('type') === 'document' && !this.allow_multiple)) {
            this.set('file', new ZenputFiles([options.add.first().toJSON()])); // for videos, we only allow one
        } else {
            var new_files = this.get('file') || new ZenputFiles();
            new_files.add(options.add.toJSON());

            // this won't trigger a change if new_files is the same object that was in 'file' before,
            // so we will trigger manually the two events backbone would have triggered if not silenced.
            // Normally, backbone triggers `change:attr` event and then `change` generic event.
            this.set('file', new_files, { silent: true });
            this.trigger('change:file', this, new_files);
            this.trigger('change', this);
        }
        done();
    },

    /**
     * Get things in order for submission. This includes unsetting 'file' values and setting them as final
     * values in 'values'.
     */
    prepare_for_submission: function () {
        const _this = this;
        var new_values = this.get('value') || [];

        if (!this.get('file')) {
            return;
        }

        // we only want want one signature value, replace the existing value with what comes in from file if that exists.
        if (new_values.length && this.get('file').length && this.get('type') === 'signature') {
            new_values = [];
        }

        this.get('file').each(function (file) {
            var raw_filename = file.get('raw_file').name;

            if (!$f.mobile.uploads.get_pending_upload(raw_filename).is_upload_finished()) {
                // @author:david if this happens, it's often because a parent_upload_guid isn't set correctly
                // in UploadableFile when checking $f.mobile.uploads.get_num_remaining_uploads()
                throw new Error('Critical: submitting form field before files are uploaded');
            }

            if ($f.mobile.uploads.get_pending_upload(raw_filename).is_upload_missing()) {
                return true; // continue - we don't want to set a filename for this
            }

            var pending_upload = $f.mobile.uploads.get_pending_upload(raw_filename);

            // @author:david @jira:MOBILE-660 before we were uploading direct-to-s3, we would generate the filename
            // locally and save it as upload_filename. The server would then update that filename, which we would save
            // as server_filename. Now, we only need upload_filename, but to handle old uploads saved from previous
            // versions of the app, we must still check server_filename first.
            //
            // tldr: we can remove the check for server_filename after awhile, as it's only necessary to handle data
            // from old apps
            var filename = pending_upload.get('server_filename') || pending_upload.get('upload_filename');

            if (!filename) {
                return true;
            }

            if ($f.flags.get_flag('detect_low_quality_photos') && _this.get('type') === 'image') {
                const lowQualityWarningIgnored = file.get('is_low_quality');

                new_values.push({
                    s3_key: filename,
                    laplacian_variance: file.get('laplacian_variance'),
                    is_low_quality: lowQualityWarningIgnored ?? false
                });

                if (lowQualityWarningIgnored) {
                    _this.collection?.smetadata.set('has_low_quality_photos', true, { silent: true });
                }
            } else {
                new_values.push(filename);
            }
        });

        this.set(
            {
                file: null,
                value: new_values.length ? new_values : ''
            },
            { silent: true }
        );
    },

    queue_field_files_for_upload: function () {
        var this_ = this;
        var files = this.get('file');

        if (!files || !files.length) {
            return;
        }

        files.each(function (file) {
            if (!file.get('raw_file')) {
                $f.mobile.logging.log_error('Skipping file upload. Missing raw_file.', {
                    url_native: file.get_url_native()
                });
                return true;
            }

            var file_path = this_.create_field_upload_filename(file.get('raw_file'));
            // @author Ivan: path gets re-created every time collection refreshes, but only the first occurrence gets uploaded
            if (!file.get('path')) {
                file.set('path', file_path, { silent: true });
            }

            $f.mobile.uploads.add_pending_file_upload({
                file: file,
                file_type: this_.get('type'),
                parent_upload_guid: this_.get_parent_upload_guid(),
                upload_filename: file_path
            });
        });
    },

    /**
     * Try to recover any broken file objects. On web, we cannot save window.File objects to localStorage (on some
     * browsers); they get corrupted and turned into POJOs. This takes a broken file POJO and replaces it
     * with a healthy File object from the active DOM, if available. This is necessary because we run
     * upload code from a form structure we've retrieved from localStorage, in which state the proper
     * File references have been lost.
     */
    _repair_corrupted_images_on_web: function () {
        if (!isWeb()) {
            return;
        }

        if (this.get('type') === 'signature') {
            return;
        }

        if (!this.get('file') || !this.get('file').length) {
            return;
        }

        var new_files = new ZenputFiles();

        this.get('file').each(function (file) {
            if (file.get('raw_file') instanceof window.File) {
                new_files.add(file);
                return true; // good to go, no problems here
            }

            var fixed_file = $f.mobile.uploads.web_filestore.get_file(file.id);

            if (!fixed_file) {
                return true; // file was loaded before a page refresh and is lost. Nothing we can do :(
            }

            new_files.add(fixed_file);
        });

        this.set('file', new_files.length ? new_files : null);
    },

    /**
     * Get the upload guid for the structure that this field is being used for. This is used to associate the
     * image(s) and/or video being uploaded here to the structure that's being uploaded (e.g., the form submission,
     * task, project, etc.).
     */
    get_parent_upload_guid: function () {
        throw new Error('Programmer Error: get_parent_upload_guid() not implemented');
    },

    create_field_upload_filename: function (file_entry) {
        throw new Error('Programmer Error: create_field_upload_filename() not implemented');
    },

    /**
     * Override toJSON() on mobile web so we never store large file urls to disk. Some files will fail to save on some
     * browsers because of localStorage limits.
     */
    toJSON: function () {
        var json = MobileMediaField.__super__.toJSON.apply(this, arguments);

        if (!isWeb()) {
            return json;
        }

        if (!json.file || !json.file.length) {
            return json;
        }

        // avoid altering the actual file object in the model by cloning our files array.
        const files = JSON.parse(JSON.stringify(json.file));

        json.file = files;
        return json;
    },

    // don't copy the value for media fields on autofill copy
    get_attributes_for_autofill: function () {
        return {};
    },

    get_submission_value: function () {
        const answerValue = this.get('value') || [];
        if ($f.flags.get_flag('detect_low_quality_photos')) {
            // Image fields have an object with one of its keys being s3_key.
            // Other media fields have just an s3 url as a string
            return answerValue.map((value) => (typeof value === 'string' ? { s3_key: value } : value));
        } else {
            return answerValue.map((s3_key) => ({ s3_key }));
        }
    }
});
